import { ExternalLink } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FilterValue } from "@/components/filter-popover";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePersonActivities } from "@/hooks/api/use-person-activities";
import { usePersonDetail } from "@/hooks/api/use-person-detail";
import { usePersonOpportunities } from "@/hooks/api/use-person-opportunities";
import { usePersonTouchpointPersons } from "@/hooks/api/use-person-touchpoint-persons";
import { useFilteredActivities } from "@/hooks/use-filtered-activities";
import { usePageTitle } from "@/hooks/use-page-title";
import { usePersonSelection } from "@/hooks/use-person-selection";
import { ActivityTable } from "@/pages/accounts/account-explorer/touchpoints-table";
import type {
  ActivityTableRef,
  FilterMode,
} from "@/pages/accounts/account-explorer/touchpoints-table/types";
import { PersonActivityCharts } from "./person-activity-charts";
import { PersonSelector } from "./person-selector";

export default function PersonExplorer() {
  // State management
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [visibleRange, setVisibleRange] = useState<{
    start: string;
    end: string;
  }>();
  const { selectedPersonId } = usePersonSelection();
  const activityTableRef = useRef<ActivityTableRef>(null);

  const { data: personDetail } = usePersonDetail(selectedPersonId);

  // Set page title based on selected person
  const personName = useMemo(() => {
    if (!personDetail) return undefined;
    const name = [personDetail.first_name, personDetail.last_name]
      .filter(Boolean)
      .join(" ");
    return name || personDetail.email_address || undefined;
  }, [personDetail]);

  usePageTitle(
    personName ? `${personName} - Person Explorer` : "Person Explorer",
  );

  const [filters, setFilters] = useState<Record<string, FilterValue>>({});
  const [customTimeRange, setCustomTimeRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  // Fetch person data
  const { data: activities, isLoading } = usePersonActivities(selectedPersonId);
  const { data: opportunities } = usePersonOpportunities(selectedPersonId);
  const { getPersonName, getPersonEmail, getNameByEmail } =
    usePersonTouchpointPersons(selectedPersonId);

  // Calculate filtered activities
  const filteredActivities = useFilteredActivities(activities, filters);

  // Create a filtered activities variable that respects the cited touchpoints filter when active
  const filteredActivitiesWithCitations = useMemo(() => {
    if (filters.citedTouchpointIds) {
      // If citedTouchpointIds filter is active, pass only those activities
      return (
        activities?.filter((act) =>
          (filters.citedTouchpointIds as string[]).includes(act.id),
        ) ?? []
      );
    } else {
      // Otherwise, use the existing logic based on filterMode
      return filterMode !== "all" ? filteredActivities : undefined;
    }
  }, [activities, filteredActivities, filters.citedTouchpointIds, filterMode]);

  // Filter handlers
  const updateFilter = useCallback(
    (key: string, value: FilterValue) => {
      setFilters((prev) => {
        if (value === null || value === undefined) {
          const newFilters = { ...prev };
          delete newFilters[key];
          return newFilters;
        }

        return { ...prev, [key]: value };
      });

      if (filterMode === "all" && value) {
        setFilterMode("highlight");
      }
    },
    [filterMode],
  );

  const clearFilter = useCallback((key: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({});
    setFilterMode("all");
    setCustomTimeRange(null);
  }, []);

  // Event handlers
  const handleDateClick = useCallback((date: string) => {
    activityTableRef.current?.scrollToDate(date);
  }, []);

  const handleVisibleRangeChange = useCallback(
    (range: { start: string; end: string }) => {
      setVisibleRange(range);
    },
    [],
  );

  // Reset filters when person changes
  const previousPersonIdRef = useRef<string | undefined>(selectedPersonId);
  useEffect(() => {
    if (selectedPersonId !== previousPersonIdRef.current) {
      resetFilters();
      previousPersonIdRef.current = selectedPersonId;
    }
  }, [selectedPersonId, resetFilters]);

  // Render
  return (
    <div className="flex flex-col h-full space-y-2 overflow-hidden">
      <div className="shrink-0">
        <div className="flex flex-col space-y-4 pt-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
          <h1 className="whitespace-nowrap font-heading">Person Explorer</h1>
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
            <PersonSelector />
            {personDetail?.accounts && personDetail.accounts.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {personDetail.accounts.slice(0, 3).map((account) => (
                  <Tooltip key={account.id}>
                    <TooltipTrigger asChild>
                      <div>
                        <Badge
                          variant="default"
                          className="cursor-pointer hover:bg-accent/80 transition-colors text-sm font-normal normal-case"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(
                              `/accounts/${account.id}/explorer`,
                              "_blank",
                            );
                          }}
                        >
                          {account.name}
                          <ExternalLink className="ml-1.5 h-3 w-3" />
                        </Badge>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View {account.name} in Account Explorer</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
                {personDetail.accounts.length > 3 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs uppercase"
                        aria-label={`View ${
                          personDetail.accounts.length - 3
                        } more accounts`}
                      >
                        +{personDetail.accounts.length - 3} more
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      collisionPadding={16}
                      className="w-80 p-2"
                    >
                      <div className="text-sm font-medium text-muted-foreground mb-2 px-2">
                        More Accounts ({personDetail.accounts.length} Total)
                      </div>
                      <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
                        {personDetail.accounts.slice(3).map((account) => (
                          <Button
                            key={account.id}
                            variant="ghost"
                            className="flex items-center justify-between gap-2 w-full text-left text-sm h-auto py-1.5"
                            onClick={() => {
                              window.open(
                                `/accounts/${account.id}/explorer`,
                                "_blank",
                              );
                            }}
                          >
                            <span className="truncate">{account.name}</span>
                            <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                          </Button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedPersonId ? (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="shrink-0">
            <PersonActivityCharts
              personId={selectedPersonId}
              onDateClick={handleDateClick}
              visibleRange={visibleRange}
              filteredActivities={filteredActivitiesWithCitations}
              customTimeRange={customTimeRange}
              setCustomTimeRange={setCustomTimeRange}
              opportunities={opportunities}
            />
          </div>
          <ActivityTable
            ref={activityTableRef}
            key={selectedPersonId}
            data={activities}
            filteredActivities={filteredActivitiesWithCitations}
            isLoading={isLoading}
            accountId={undefined} // Not needed for person explorer
            selectedOpportunityIds={undefined}
            filterMode={filterMode}
            setFilterMode={setFilterMode}
            filters={filters}
            updateFilter={updateFilter}
            clearFilter={clearFilter}
            clearAllFilters={clearAllFilters}
            onVisibleRangeChange={handleVisibleRangeChange}
            totalTouchpoints={activities?.length || 0}
            milestoneTasks={undefined} // No milestone analyses for person explorer
            customGroupOpportunityIds={undefined}
            storageKeyPrefix="persons::explorer"
            opportunitiesOverride={opportunities}
            getPersonNameOverride={getPersonName}
            getPersonEmailOverride={getPersonEmail}
            getNameByEmailOverride={getNameByEmail}
          />
        </div>
      ) : (
        <div className="flex-1">
          <EmptyState
            title="Select a person to explore"
            description="Choose a person to view their story and activities."
          />
        </div>
      )}
    </div>
  );
}
